import { useCallback, useEffect, useMemo, useState } from 'react'
import type { MouseEvent } from 'react'
import { MuseArrowRight } from 'src/components/Icons/MuseArrowRight'
import CloseIcon from 'src/images/icons/CloseIcon'
import { openShoppingMuse } from 'src/sdk/muse/openShoppingMuse'
import { useMuseEntryPointsConfig } from 'src/sdk/muse/useMuseEntryPointsConfig'
import { getResponsiveVisibility } from 'src/utils/getResponsiveVisibility'

import { MusePdpAvatar } from './MusePdpAvatar'

const STORAGE_KEY = 'muse-pdp-entry-collapsed'

const TITLE = 'Está com dúvidas sobre esse produto?'
const SUBTITLE = 'Fale com nosso vendedor virtual'
const CTA = 'Falar com o vendedor virtual'
const MOBILE_PROMPT = 'Precisa de algo?'

function readCollapsed(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function writeCollapsed(collapsed: boolean) {
  try {
    sessionStorage.setItem(STORAGE_KEY, collapsed ? 'true' : 'false')
  } catch {
    // sessionStorage can be unavailable (privacy mode)
  }
}

export function MusePdpEntryPoint() {
  const config = useMuseEntryPointsConfig()
  const pdp = config?.pdp
  const [isCollapsed, setIsCollapsed] = useState(false)

  useEffect(() => {
    setIsCollapsed(readCollapsed())
  }, [])

  const visibilityClasses = useMemo(() => {
    return getResponsiveVisibility(pdp?.showOnDesktop, pdp?.showOnMobile)
  }, [pdp?.showOnDesktop, pdp?.showOnMobile])

  const collapse = useCallback(() => {
    setIsCollapsed(true)
    writeCollapsed(true)
  }, [])

  const handleDismiss = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      collapse()
    },
    [collapse]
  )

  const expand = useCallback(() => {
    setIsCollapsed(false)
    writeCollapsed(false)
  }, [])

  if (!pdp?.enableSession) {
    return null
  }

  if (isCollapsed) {
    return (
      <button
        type="button"
        data-testid="muse-pdp-avatar"
        data-dy-id="muse-pdp-entry"
        aria-label="Abrir vendedor virtual"
        className={`dy-chat-cta fixed bottom-24 right-md z-[45] size-12 rounded-round border-0 bg-transparent p-0 restructure-small-desktop:bottom-md restructure-small-desktop:size-16 ${visibilityClasses}`}
        onClick={expand}
      >
        <MusePdpAvatar />
      </button>
    )
  }

  return (
    <div data-dy-id="muse-pdp-entry">
      <div
        data-testid="muse-pdp-card"
        className={`fixed bottom-24 right-md z-[45] flex flex-col items-end pr-lg restructure-small-desktop:hidden ${visibilityClasses}`}
      >
        <aside className="w-[224px] overflow-hidden rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl border border-solid border-neutral04 bg-restructure-background-primary shadow-[0px_0px_12px_2px_rgba(37,52,221,0.12)]">
          <div className="flex items-center justify-between px-sm py-xs">
            <p className="dy-muse-widget-container-modal text-font-size-3 font-bold text-secondary">
              {MOBILE_PROMPT}
            </p>
            <button
              type="button"
              data-testid="muse-pdp-dismiss"
              data-dy-id="muse-pdp-dismiss"
              className="flex size-[18px] shrink-0 items-center justify-center border-0 bg-transparent p-0 text-secondary"
              aria-label="Fechar vendedor virtual"
              onClick={handleDismiss}
            >
              <CloseIcon width={18} height={18} fill="currentColor" />
            </button>
          </div>
          <button
            type="button"
            data-dy-id="muse-pdp-cta"
            data-testid="muse-pdp-cta"
            className="dy-chat-cta flex w-full items-center gap-sm bg-[#edeef9] p-sm text-left text-font-size-2 font-semibold text-restructure-action"
            onClick={openShoppingMuse}
          >
            {CTA}
            <MuseArrowRight />
          </button>
        </aside>
        <div className="mt-[7px] translate-x-1/2" aria-hidden>
          <MusePdpAvatar />
        </div>
      </div>
      <div
        data-testid="muse-pdp-card-desktop"
        className={`fixed bottom-md right-md z-[45] hidden flex-col items-end pr-xl restructure-small-desktop:flex ${visibilityClasses}`}
      >
        <aside className="w-[280px] overflow-hidden rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl border border-solid border-neutral04 bg-restructure-background-primary shadow-[0px_0px_12px_2px_rgba(37,52,221,0.12)]">
          <div className="relative flex w-full items-center gap-2.5 px-lg py-md">
            <div className="flex min-w-0 flex-1 flex-col gap-xxs">
              <p className="dy-muse-widget-container-modal text-font-size-3 font-bold text-secondary">
                {TITLE}
              </p>
              <p className="dy-muse-widget-container-modal text-font-size-2 font-medium text-secondary">
                {SUBTITLE}
              </p>
            </div>
            <button
              type="button"
              data-testid="muse-pdp-dismiss-desktop"
              data-dy-id="muse-pdp-dismiss"
              className="absolute right-sm top-sm flex size-[18px] items-center justify-center text-secondary"
              aria-label="Fechar vendedor virtual"
              onClick={handleDismiss}
            >
              <CloseIcon width={18} height={18} fill="currentColor" />
            </button>
          </div>
          <button
            type="button"
            data-dy-id="muse-pdp-cta"
            data-testid="muse-pdp-cta"
            className="dy-chat-cta flex w-full items-center justify-center gap-sm bg-[#edeef9] p-md text-left text-font-size-2 font-semibold text-restructure-action"
            onClick={openShoppingMuse}
          >
            {CTA}
            <MuseArrowRight />
          </button>
        </aside>
        <div className="mt-[7px] translate-x-1/2" aria-hidden>
          <MusePdpAvatar />
        </div>
      </div>
    </div>
  )
}
